import { defineNuxtPlugin, navigateTo, useNuxtApp, useRequestEvent, useRuntimeConfig } from 'nuxt/app';
import { grantTypeTransform, setLoginCookies } from 'awServerModule/runtime/util/grantsHelper';
import type { AwAxCache } from './awAxCache.js';
import { AW_AX_CACHE_DEFAULT, AW_AX_CACHE_GLOBAL, AW_AX_CACHE_SEGMENTATION } from './awAxCache';
import { type AwExtendedAxiosInstance, createApiNetwork, createBeAuthenticationNetwork, createFeApiNetwork, createMsgTokenNetwork } from './network.js';

import { useNotificationStore } from '~~/common/stores/notification';
import { useAuthenticationStore } from '~~/common/stores/authentication';
import { useAwCacheKeyStore } from '~~/common/stores/awCacheKey';
import { useUserStore } from '~~/common/stores/user';
import { useDeliveryStore } from '~~/shop/app/stores/delivery';
import { useLoyaltyCardStore } from '~~/shop/app/stores/loyaltyCard';
import { handleRedirectAfterTokenExpired } from '~~/common/server/globalMiddlewareConstants';
import { type LoginType, LT_ANON } from '~~/common/config/LoginTypeConfig';
import { AGT_ANONYMOUS, AGT_REFRESH_TOKEN } from '~~/common/config/AuthGrantType';
import { isAxiosError } from 'axios';
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { createProcessMessaging } from '~~/common/utils/processMessaging';
import { asStringCookie } from '~~/common/utils/cookie';
import { PROCESS_MESSAGE_NOTIFY_REQUEST } from '~~/common/config/processMessaging';
import type { AwNuxtAppExtensions } from '~~/common/types/nuxt-app-extensions';
import { useAwRequestTracing } from '~~/common/composables/useAwRequestTracing';

type AwAxiosRequestConfig = InternalAxiosRequestConfig & { awAxCache?: AwAxCache, stopRecursion?: boolean, segmentationCacheKeyChange?: string, awAuthRetried?: boolean };
type AwAxiosResponse = AxiosResponse & { config: AwAxiosRequestConfig, awAxCacheHit?: AwAxCache | false };

export interface TokenResponse {
  access_token?: string;
  token_type?: string;
  login_type?: LoginType;
  refresh_token?: string;
  expires_in?: number;

  [key: string]: unknown;
}

const QUERY_STRING_CACHE_SEGMENTATION_CODE = 'cacheSegmentationCode';

const requestWithoutDeliveryAreaParams = new Set(['/config']);

/**
 * Why a refresh produced no token. Both cases used to collapse into a bare `{}`,
 * which callers could not tell apart from a successful refresh.
 */
export type TokenRefreshFailureReason =
  /** SSR-only: there is no BE authentication network to ask (client-side render). */
  | 'noAuthenticationNetwork'
  /** The token endpoint rejected and the error was not a recoverable invalid-refresh-token. */
  | 'refreshRejected';

export type TokenRefreshResult =
  | { ok: true, token: TokenResponse }
  | { ok: false, reason: TokenRefreshFailureReason };

function createPluginProvides () {
  const nuxtApp = useNuxtApp();
  const {
    $i18n,
    $localePath,
    $cookies,
    $awt,
    $awMergedConfig: {
      xDebug,
    },
  } = nuxtApp;
  const $logger = nuxtApp.$logger;
  const runtimeConfig = useRuntimeConfig();
  const reqHeaders = useRequestEvent()?.node?.req?.headers;
  const proxiedHeaders = { ...reqHeaders || null };
  if (reqHeaders) {
    for (const h of [
      // list of headers is from:
      // https://github.com/nuxt-community/axios-module/blob/2436566b06638aa204a10e48ae63cdc58d7ba9a1/lib/module.js#L82
      'accept',
      'cf-connecting-ip',
      'cf-ray',
      'content-length',
      'content-md5',
      'content-type',
      'host',
      'x-forwarded-host',
      'x-forwarded-port',
      'x-forwarded-proto',
    ]) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete proxiedHeaders[h];
    }
  }

  const feApiNetwork: AwExtendedAxiosInstance = createFeApiNetwork(proxiedHeaders);
  const beAuthenticationNetwork: AwExtendedAxiosInstance | null = (import.meta.server
    ? createBeAuthenticationNetwork(proxiedHeaders)
    : null
  );
  const apiNetwork: AwExtendedAxiosInstance = createApiNetwork(proxiedHeaders);
  const messageTokenNetwork: AwExtendedAxiosInstance = createMsgTokenNetwork(proxiedHeaders);

  const { setAwTracingHeaders } = useAwRequestTracing();

  const refreshCalls = {
    current: 0,
    max: 3,
  };
  let refreshingCall: Promise<TokenRefreshResult> | null = null;
  let prePersistLoginCookiesToStore: TokenResponse | null = null;
  const awCacheKeyStore = useAwCacheKeyStore();
  const authenticationStore = useAuthenticationStore();
  const notificationStore = useNotificationStore();
  const userStore = useUserStore();
  const loyaltyCardStore = useLoyaltyCardStore();
  const deliveryStore = useDeliveryStore();

  function refreshTokens (payload: Parameters<typeof grantTypeTransform>[0]['body']) {
    if (refreshingCall) {
      return refreshingCall;
    }

    if (refreshCalls.max < refreshCalls.current) {
      const errorMessage = `Authentication failed ${refreshCalls.max} times!`;
      authenticationStore.clearAuthCookies().then(() => {
        prePersistLoginCookiesToStore = null;
        userStore.setLoginType(LT_ANON);
        userStore.resetUserData();
        loyaltyCardStore.resetLoyaltyData();
        deliveryStore.fetchMethod();

        return Promise.reject(new Error(errorMessage));
      });
    }

    refreshCalls.current++;

    const currentCall = (async (): Promise<TokenRefreshResult> => {
      if (!import.meta.server) {
        return { ok: true, token: await feApiNetwork.$post<TokenResponse>('/get-token', payload) };
      }
      const responseBody = await beAuthenticationNetwork?.$post<TokenResponse>('/token', grantTypeTransform({
        body: payload,
        cookies: {
          get: (key: string) => $cookies.get(key),
        },
        clientId: runtimeConfig.auchanApiClientId,
        clientSecret: runtimeConfig.auchanApiClientSecret,
      }));
      // NOTE: an absent body still has to run the side effects below — setLoginCookies
      // with an all-undefined body *removes* the auth cookies, which is intentional.
      const body: TokenResponse = responseBody ?? {};
      const { isChangeAsync } = setLoginCookies({
        cookies: $cookies,
        body: {
          login_type: body.login_type,
          token_type: body.token_type,
          expires_in: body.expires_in,
          access_token: body.access_token,
          refresh_token: body.refresh_token,
        },
      });
      if (body.refresh_token) {
        delete body.refresh_token;
      }
      prePersistLoginCookiesToStore = isChangeAsync ? body : null;
      setAxiosTokenFromCookie();
      return responseBody
        ? { ok: true, token: responseBody }
        : { ok: false, reason: 'noAuthenticationNetwork' };
    })().then((data) => {
      refreshCalls.current = 0;
      return Promise.resolve(data);
    }).catch((error: unknown): TokenRefreshResult | Promise<TokenRefreshResult> => {
      if (isInvalidRefreshTokenError(error)) {
        userStore.setLoginType(LT_ANON);
        userStore.resetUserData();
        loyaltyCardStore.resetLoyaltyData();
        refreshingCall = null;
        const refreshPromise = refreshTokens({ grant_type: AGT_ANONYMOUS });
        if (import.meta.client) {
          notificationStore.pushError({
            text: {
              title: $awt('aw.token_expired'),
            },
            manualClose: undefined,
            screenReaderOnly: undefined,
            promise: undefined,
          });
          setTimeout(() => {
            navigateTo($localePath('/'));
          }, 3000);
        } else {
          userStore.setForceLogoutToRedirect();

          //NOTE: this is a hack, the global middleware won't triggered if the user has an invalid access token, so we need to manually redirect the user the /LOCALE/?t=UUID page
          handleRedirectAfterTokenExpired($i18n, $cookies, to => nuxtApp.$router.push(to));
        }
        return refreshPromise;
      }
      return { ok: false, reason: 'refreshRejected' };
    }).finally(() => {
      refreshingCall = null;
    });
    refreshingCall = currentCall;
    return currentCall;
  }

  function setAxiosTokenFromCookie () {
    const accessToken = prePersistLoginCookiesToStore?.access_token || $cookies.get('access_token');
    const tokenType = prePersistLoginCookiesToStore?.token_type || $cookies.get('token_type');
    const authHeader = `${tokenType ?? ''} ${accessToken ?? ''}`;
    apiNetwork.defaults.headers.Authorization = authHeader;
    feApiNetwork.defaults.headers.Authorization = authHeader;

    const loginType = asStringCookie(prePersistLoginCookiesToStore?.login_type || $cookies.get('login_type'));
    if (loginType) {
      userStore.setLoginType(loginType);
    }
  }

  async function handleUnauthorizedRequest (axNetwork: AwExtendedAxiosInstance, errorCall: AxiosError) {
    const errorConfig: AwAxiosRequestConfig | undefined = errorCall.config;
    // Without a config there is nothing to replay, and awAuthRetried means we already
    // replayed this request once after a refresh — a second retry would 401 again and
    // re-enter this handler through the response interceptor, which never terminates
    // while the refresh itself keeps succeeding.
    if (!errorConfig || errorConfig.awAuthRetried) {
      throw errorCall;
    }
    const userIsLoggedIn = $cookies.get('login_type') !== LT_ANON;
    try {
      const result = await refreshTokens({ grant_type: userIsLoggedIn ? AGT_REFRESH_TOKEN : AGT_ANONYMOUS });
      if (!result.ok) {
        // We have no new token. Retrying would blank the shared Authorization header,
        // 401 again and re-enter this handler, so fail with the original error and let
        // the catch below decide whether an anonymous refresh can still recover it.
        throw errorCall;
      }
      const authHeader = `${result.token.token_type ?? ''} ${result.token.access_token ?? ''}`;
      apiNetwork.defaults.headers.Authorization = authHeader;
      feApiNetwork.defaults.headers.Authorization = authHeader;
      errorConfig.headers.Authorization = authHeader;
      errorConfig.baseURL = undefined;
      errorConfig.awAuthRetried = true;

      return await axNetwork.request(errorConfig);
    } catch (error) {
      const responseStatus = isAxiosError(error) ? error.response?.status : undefined;
      if (responseStatus === 401 && refreshCalls.max < refreshCalls.current) {
        throw error;
      } else if (
        (errorCall.response?.status ?? 0) >= 500
        || (responseStatus === 401 && isInvalidRefreshTokenError(error))
      ) {
        return await refreshTokens({ grant_type: AGT_ANONYMOUS });
      } else {
        throw error;
      }
    }
  }

  apiNetwork.interceptors.request.use((config: AwAxiosRequestConfig) => {
    configureCache(config);
    const {
      urlPath,
      urlQuery,
    } = getUrlSections(config.url);

    urlQuery.set('hl', $i18n.locale.value);

    config.url = `${urlPath}?${urlQuery}`;
    configureXDebug(config);
    configureCookies(config);
    setAwTracingHeaders(config);
    return config;
  });

  feApiNetwork.interceptors.request.use((config: AwAxiosRequestConfig) => {
    configureXDebug(config);
    configureCookies(config);
    setAwTracingHeaders(config);
    return config;
  });

  if (import.meta.server) {
    beAuthenticationNetwork?.interceptors.request.use((config: AwAxiosRequestConfig) => {
      config.headers['Accept-Language'] = $i18n.locale.value;
      configureXDebug(config);
      configureCookies(config);
      return config;
    });
  }

  apiNetwork.interceptors.response.use((response: AwAxiosResponse) => {
    createProcessMessaging(PROCESS_MESSAGE_NOTIFY_REQUEST).send({});
    if (isCacheHit({
      isCacheRequested: Boolean(response.config.awAxCache),
      responseHeaders: response.headers,
      $logger,
    })) {
      response.awAxCacheHit = response.config.awAxCache;
    } else {
      response.awAxCacheHit = false;
    }
    return response;
  }, (error: AxiosError) => {
    createProcessMessaging(PROCESS_MESSAGE_NOTIFY_REQUEST).send({});
    if (error?.response?.status === 409) {
      const errorConfig = error?.config as AwAxiosRequestConfig | undefined;
      if (errorConfig?.stopRecursion) {
        $logger.error(new CacheSegmentationError(`stopRecursion reached on ${errorConfig?.url}`));
        return Promise.reject(error);
      }
      const segmentationCacheKey = extractNewSegmentationCacheKey({ responseHeaders: error.response.headers });
      if (segmentationCacheKey && errorConfig) {
        awCacheKeyStore.setCacheKeySegmentation(segmentationCacheKey[1] ? `${segmentationCacheKey[1]}` : '');
        const urlSearch = new URLSearchParams(errorConfig.url!.split('?')[1]);
        const newCacheKey = awCacheKeyStore.cacheKeySegmentation;
        if (urlSearch.get(QUERY_STRING_CACHE_SEGMENTATION_CODE) !== newCacheKey) {
          urlSearch.set(QUERY_STRING_CACHE_SEGMENTATION_CODE, newCacheKey);
          errorConfig.segmentationCacheKeyChange = newCacheKey;
          errorConfig.url = `${errorConfig.url!.split('?')[0]}?${urlSearch}`;
          errorConfig.stopRecursion = true;

          return apiNetwork.request(errorConfig);
        } else {
          $logger.error(new CacheSegmentationError(`unchanged cacheKey on ${errorConfig?.url}`));
        }
      }
    } else if (error?.response?.status === 401) {
      handleUnauthorizedRequest(apiNetwork, error).catch((errorHandlerError: Error) => {
        $logger.error('Unauthorized API Network', errorHandlerError);
      });
    } else if (error?.response?.status && error.response.status >= 500) {
      $logger.error('Api Network', error);
    }
    return Promise.reject(error);
  });

  feApiNetwork.interceptors.response.use((response: AxiosResponse) => {
    setAxiosTokenFromCookie();
    return response;
  }, (error: AxiosError) => {
    const status = error.response?.status ?? null;

    if (status === 401) {
      $logger.error('Unauthorized Authentication', error);
    } else if (status && status >= 500) {
      $logger.error('Authentication Network', error);
    }

    return Promise.reject(error);
  });

  if (import.meta.server) {
    beAuthenticationNetwork?.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: Error) => {
        $logger.error(new BeAuthenticationNetworkError('Authorization', { cause: error }));
        return Promise.reject(error);
      },
    );
  }

  setAxiosTokenFromCookie();

  function getUrlSections (url?: string) {
    const [urlPath, urlQuery] = (url || '').split('?');
    return {
      urlPath,
      urlQuery: new URLSearchParams(urlQuery),
    };
  }

  function configureCache (config: AwAxiosRequestConfig) {
    if (!config.url) {
      return;
    }
    const appendPrefix = (url: string, prefix: string, fallback: string) => url.startsWith(prefix) ? fallback : prefix;
    const getCachePrefix = (url: string) => appendPrefix(url, '/cache', '');
    const getStartingSlash = (url: string) => appendPrefix(url, '/', '');
    const getQuerySeparator = (url: string) => url.includes('?') ? '&' : '?';
    if (config.awAxCache === AW_AX_CACHE_SEGMENTATION) {
      const {
        urlPath,
        urlQuery,
      } = getUrlSections(config.url);
      urlQuery.set(QUERY_STRING_CACHE_SEGMENTATION_CODE, awCacheKeyStore.cacheKeySegmentation);
      config.url = `${getCachePrefix(config.url)}${getStartingSlash(config.url)}${urlPath}?${urlQuery}`;
    }
    // AW_AX_CACHE_GLOBAL and AW_AX_CACHE_DEFAULT both hit the /cache prefix; they
    // differ only in that GLOBAL does not append the delivery-area params below.
    if (config.awAxCache === AW_AX_CACHE_DEFAULT || config.awAxCache === AW_AX_CACHE_GLOBAL) {
      config.url = `${getCachePrefix(config.url)}${getStartingSlash(config.url)}${config.url}`;
    }
    if ((!config.awAxCache || config.awAxCache === AW_AX_CACHE_DEFAULT) && !requestWithoutDeliveryAreaParams.has(config.url) && !config.url.includes(awCacheKeyStore.cacheKeyLegacy)) {
      config.url += `${getQuerySeparator(config.url)}${awCacheKeyStore.cacheKeyLegacy}`;
    }
  }

  function configureCookies (config: AwAxiosRequestConfig) {
    if (import.meta.server) {
      try {
        config.headers.cookie = Object.entries($cookies.getAll()).filter(([key]) => key).map(([key, value]) => `${key}=${value}`).join('; ');
      } catch (error) {
        $logger.error('Error getting cookies:', error);
      }
    }
  }

  function configureXDebug (config: AwAxiosRequestConfig) {
    if (xDebug && config.url && !config.url.includes('XDEBUG=')) {
      const querySeparator = (config.url.includes('?') ? '&' : '?');
      config.url = `${config.url}${querySeparator}XDEBUG=PHP_STORM`;
    }
  }

  return {
    api: apiNetwork,
    feApiNetwork: feApiNetwork,
    axiosRefreshTokens: refreshTokens,
    messageTokenNetwork: messageTokenNetwork,
  };
}

export type AxiosPluginProvides = ReturnType<typeof createPluginProvides>;

export default defineNuxtPlugin(() => {
  return {
    provide: createPluginProvides(),
  };
});

function isCacheHit ({
  isCacheRequested,
  responseHeaders,
  $logger,
}: {
  isCacheRequested: boolean,
  responseHeaders: AxiosResponse['headers'],
  $logger: AwNuxtAppExtensions['$logger'],
}) {
  const isCacheHitBasedOnResponse = Object.entries(responseHeaders).some(([headerName, headerValue]) => {
    return headerName?.toLowerCase?.() === 'x-cached' && headerValue?.toLowerCase?.() === 'hit';
  });
  if (!isCacheRequested && isCacheHitBasedOnResponse) {
    $logger.warn('Got a response with Cache Hit, for an endpoint that was not supposed to be cached.');
  }
  return isCacheHitBasedOnResponse;
}

/**
 * True when the token endpoint rejected with an invalid-refresh-token payload.
 * Takes `unknown` because a rejected request can be anything — a non-Axios error, a
 * TypeError from our own handler — not necessarily an AxiosError with a response body.
 */
function isInvalidRefreshTokenError (error: unknown): boolean {
  const responseData = isAxiosError(error) ? error.response?.data : undefined;
  return Boolean(
    responseData
    && typeof responseData === 'object'
    && 'error' in responseData
    && responseData.error === 'invalid_request'
    && 'error_description' in responseData
    && typeof responseData.error_description === 'string'
    && responseData.error_description.toLowerCase().includes('refresh token is invalid'),
  );
}

function extractNewSegmentationCacheKey ({ responseHeaders }: { responseHeaders: Record<string, unknown> }) {
  return Object.entries(responseHeaders).find(([headerName]) => {
    return headerName?.toLowerCase?.() === 'x-valid-cache-segmentation-code';
  });
}

class CacheSegmentationError extends Error {
  constructor (message: string, ...rest: (ErrorOptions | undefined)[]) {
    super(message, ...rest);
    this.name = 'CacheSegmentationError';
  }
}

class BeAuthenticationNetworkError extends Error {
  constructor (message: string, ...rest: (ErrorOptions | undefined)[]) {
    super(message, ...rest);
    this.name = 'BeAuthenticationNetworkError';
  }
}

